import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    enquiryType: 'Customer Enquiry',
    fullName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#F5F1E8] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-white rounded-full text-sm text-[#8B6F47] uppercase tracking-wider">
              Get In Touch
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-[#2C2416] mb-6">
            LET'S CONNECT
          </h1>
          <p className="text-xl text-[#5C4033] mb-4">
            From sunrise, to beyond. Whether you're a customer, partner, or changemaker — we're here.
          </p>
          <p className="text-lg text-[#8B6F47]">
            Nutritious fuel for hours. Ready in seconds. Responsive in hours.
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#F5F1E8] rounded-full text-sm text-[#8B6F47] uppercase tracking-wider mb-6">
              Reach Out
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              Every message <br />matters.
            </h2>
            <p className="text-xl text-[#5C4033] max-w-3xl mx-auto">
              We read every enquiry personally. Whether you're asking about nutrition, exploring a partnership, 
              or want to join GUAG/PROJECT — we're listening.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-[#F5F1E8] rounded-2xl p-8 text-center">
              <div className="mb-4">
                <Mail className="mx-auto text-[#8B6F47]" size={40} />
              </div>
              <h3 className="text-lg font-bold text-[#2C2416] mb-2">Email</h3>
              <a href="mailto:hello@getguag.com" className="text-[#8B6F47] hover:text-[#5C4033] transition-colors">
                hello@getguag.com
              </a>
            </div>
            <div className="bg-[#F5F1E8] rounded-2xl p-8 text-center">
              <div className="mb-4">
                <MapPin className="mx-auto text-[#8B6F47]" size={40} />
              </div>
              <h3 className="text-lg font-bold text-[#2C2416] mb-2">Location</h3>
              <p className="text-[#5C4033]">South Africa</p>
            </div>
            <div className="bg-[#F5F1E8] rounded-2xl p-8 text-center">
              <div className="mb-4">
                <Clock className="mx-auto text-[#8B6F47]" size={40} />
              </div>
              <h3 className="text-lg font-bold text-[#2C2416] mb-2">Response Time</h3>
              <p className="text-[#5C4033]">Within 24-48 hours</p>
            </div>
          </div>

          <div className="bg-[#F5F1E8] rounded-2xl p-8 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-[#2C2416] mb-4">Our Commitment</h3>
            <p className="text-lg text-[#5C4033]">
              Real people. Real responses. No automated runaround.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-12">
            {/* Enquiry Type */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Nature of Enquiry <span className="text-red-500">*</span>
              </label>
              <select
                name="enquiryType"
                value={formData.enquiryType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              >
                <option value="Customer Enquiry">Customer Enquiry - Questions about products, orders, or your GUAG experience</option>
                <option value="Organisation / Corporate">Organisation / Corporate - Bulk orders, corporate wellness, or business enquiries</option>
                <option value="Partnership Opportunity">Partnership Opportunity - Retail partnerships, distribution, or collaboration</option>
                <option value="GUAG/PROJECT">GUAG/PROJECT - School feeding programmes, donations, or social impact</option>
                <option value="Media / Press">Media / Press - Press enquiries, interviews, or media requests</option>
                <option value="General Enquiry">General Enquiry - Anything else we can help with</option>
              </select>
            </div>

            {/* Full Name */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              />
            </div>

            {/* Email */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              />
            </div>

            {/* Phone */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              />
            </div>

            {/* Company */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Company / Organisation (If applicable)
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              />
            </div>

            {/* Subject */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              />
            </div>

            {/* Message */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-[#2C2416] mb-3">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#8B6F47] focus:outline-none text-[#5C4033]"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-lg"
            >
              Send Message
            </button>

            <p className="text-sm text-gray-500 mt-4 text-center">
              By submitting this form, you agree to our{' '}
              <a href="#" className="text-[#8B6F47] hover:text-[#5C4033]">Privacy Policy</a>. 
              We respect your data.
            </p>
          </form>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2416] mb-6">
              Looking for something specific?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/nutrition" className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3 group-hover:text-[#8B6F47] transition-colors">
                Shop GUAG
              </h3>
              <p className="text-[#5C4033] mb-4">
                Browse our products and place an order
              </p>
              <span className="text-[#8B6F47] font-semibold">View Products →</span>
            </Link>

            <Link to="/project" className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3 group-hover:text-[#8B6F47] transition-colors">
                GUAG/PROJECT
              </h3>
              <p className="text-[#5C4033] mb-4">
                Learn about our social impact initiative
              </p>
              <span className="text-[#8B6F47] font-semibold">Learn More →</span>
            </Link>

            <Link to="/about" className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-lg transition-shadow group">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3 group-hover:text-[#8B6F47] transition-colors">
                About GUAG
              </h3>
              <p className="text-[#5C4033] mb-4">
                Our story, mission, and values
              </p>
              <span className="text-[#8B6F47] font-semibold">Read Story →</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;